import styled from 'vue-styled-components';

import AutoCompleteTags from '@/components/Proposal/AutoCompleteTags';
import Slider from '@/components/Common/Slider';
import Remarketing, { checkRemarketingDirty } from './Remarketing';

import { LocationService } from '@/services';

import COLORS from '@design/colors.js';
import { isValueExist } from '@/utils/utils';

import InfoIcon from '../../../../public/static/assets/img/svg_icons/info_line.svg';

import { FLEX_COLUMN_DIV, GRAY_100_DIV } from '@styles/common.styled.component';

const checkLookalikeDirty = ({ origin, starting_ratio, ratio, country }) => {
    if (isValueExist(origin) && checkRemarketingDirty(origin)) return true;
    if (!isValueExist(country)) return true;
    if (starting_ratio === undefined) return true;
    if (!ratio) return true;
    return false;
};

const MARKS = {
    0: '0%',
    1: '1%',
    2: '2%',
    3: '3%',
    4: '4%',
    5: '5%',
    6: '6%',
    7: '7%',
    8: '8%',
    9: '9%',
    10: '10%',
};

export default {
    name: 'Lookalike',
    props: {
        data: {
            type: Object,
            default() {
                return {};
            },
        },
    },
    data() {
        return {
            countryText: '',
        };
    },
    computed: {
        placeholder() {
            const _text = isValueExist(this.data.country)
                ? this.$t('ONBOARDING.location_input_placeholder')
                : this.$t('ONBOARDING.location_input_placeholder_empty_tag');

            return `\uf067 ${_text}`;
        },
        hasAudience() {
            return R.or(
                R.pathOr(
                    false,
                    ['data', 'origin', 'inclusions', 'length'],
                    this
                ),
                R.pathOr(
                    false,
                    ['data', 'origin', 'exclusions', 'length'],
                    this
                )
            );
        },
    },
    methods: {
        makeRefiendCountryValue(value) {
            if (isValueExist(value)) {
                return Array.isArray(value) ? value : [value];
            } else {
                return [];
            }
        },
        async handleAdd(value, results) {
            if (!isValueExist(value)) {
                return;
            }
            const location = R.find(R.equals(value), results);

            if (isValueExist(location)) {
                const {
                    address_components,
                } = await LocationService.getCoordinates(location.place_id);

                const countryCode = R.compose(
                    R.prop('short_name'),
                    R.find(R.compose(R.includes('country'), R.prop('types')))
                )(address_components);

                this.setData(
                    ['country'],
                    [
                        ...this.makeRefiendCountryValue(this.data.country),
                        {
                            value: countryCode,
                            display_name: location.text,
                        },
                    ]
                );
            }
        },
        handleRemove({ index }) {
            this.setData(
                ['country'],
                Array.isArray(this.data.country)
                    ? R.remove(index, 1, this.data.country)
                    : []
            );
        },
        setData(route, value) {
            let data = this.data;
            data = R.assocPath(route, value, data);

            this.$emit('update:data', {
                ...data,
                meta: { dirty: checkLookalikeDirty(data) },
            });
        },
        onChangeAudienceSizeValue(ratio = 1) {
            if (ratio === 0) ratio = this.data.ratio;
            this.setData([], {
                ...this.data,
                starting_ratio: 0,
                ratio,
            });
        },
        renderAutoComplete() {
            const props = {
                attrs: {
                    ...R.pick(['handleAdd', 'handleRemove'], this),
                    countryOnly: true,
                    hasMultiple: false,
                    dirty: !isValueExist(this.data.country),
                    placeholder: `\uf067 ${this.$t(
                        'ONBOARDING.location_input_placeholder'
                    )}`,
                    api: (q, address) =>
                        LocationService.autocomplete(
                            q,
                            address,
                            1 // Country only
                        ),
                    tagValueGetter: R.prop('display_name'),
                    values: this.makeRefiendCountryValue(this.data.country),
                    tagColor: COLORS['blue-10'],
                    fontColor: COLORS['blue-60'],
                },
            };
            return <AutoCompleteTags {...props} />;
        },
        renderLocationSelect() {
            return (
                <LocationWrapper>
                    <LocationTitle>
                        {this.$t('REMARKETING.header_title_location')}
                    </LocationTitle>
                    {this.renderAutoComplete()}
                    {!isValueExist(this.data.country) && (
                        <ErrorMessage>
                            {this.$t('REMARKETING.no_field')}
                        </ErrorMessage>
                    )}
                </LocationWrapper>
            );
        },
        renderAudienceSize() {
            const { ratio = 1 } = this.data;
            const sliderProps = {
                attrs: {
                    range: false,
                    min: 0,
                    max: 10,
                    value: ratio,
                    dots: false,
                    marks: MARKS,
                    step: 1,
                },
                on: {
                    change: this.onChangeAudienceSizeValue,
                },
            };
            return (
                <LocationWrapper>
                    <LocationTitle>
                        {this.$t('REMARKETING.header_title_size')}
                        <AudienceSizeDetail>
                            {this.data.starting_ratio} - {this.data.ratio} %
                        </AudienceSizeDetail>
                    </LocationTitle>
                    <Slider {...sliderProps} />
                    <LocationDescription>
                        <InfoIcon
                            v-view-box
                            fill={COLORS['gray-50']}
                            style={{
                                marginRight: '8px',
                                marginTop: '1px',
                                minWidth: '16px',
                                height: '16px',
                            }}
                        />
                        {this.$t('REMARKETING.location_description')}
                    </LocationDescription>
                    {(this.data.starting_ratio === undefined ||
                        !this.data.ratio) && (
                        <ErrorMessage>
                            {this.$t('REMARKETING.no_field')}
                        </ErrorMessage>
                    )}
                </LocationWrapper>
            );
        },
    },
    created() {
        if (this.data.starting_ratio > 0) {
            setTimeout(() => {
                this.onChangeAudienceSizeValue(this.data.ratio);
            }, 200);
        } else if (this.data.starting_ratio === undefined && !this.data.ratio) {
            setTimeout(() => {
                this.onChangeAudienceSizeValue(1);
            }, 200);
        }
    },
    render() {
        const props = {
            attrs: {
                data: this.data.origin,
                type: 'LOOKALIKE',
            },
            on: {
                'update:data': (origin) => {
                    this.setData(['origin'], R.omit(['meta'], origin));
                },
            },
        };
        return (
            <Wrapper>
                <Title>{this.$t('REMARKETING.header_title_lookalike')}</Title>
                <Remarketing {...props} />
                {this.hasAudience
                    ? [this.renderLocationSelect(), this.renderAudienceSize()]
                    : ''}
            </Wrapper>
        );
    },
};

const Wrapper = styled(FLEX_COLUMN_DIV)`
    align-items: flex-start;
    width: 100%;
    margin-top: 24px;
`;

const Title = styled.p`
    color: ${COLORS['gray-100']};
    font-size: 14px;
    font-weight: 500;
`;

const LocationWrapper = styled(FLEX_COLUMN_DIV)`
    width: 100%;
    margin-top: 24px;
`;

const LocationTitle = styled(GRAY_100_DIV)`
    font-size: 14px;
    margin-bottom: 12px;
    display: flex;
    width: 100%;
    justify-content: space-between;
`;

const LocationDescription = styled.div`
    align-items: flex-start;
    display: flex;
    font-size: 13px;
    color: ${COLORS['gray-50']};
    margin-top: 16px;
`;

const AudienceSizeDetail = styled.p`
    color: ${COLORS['blue-60']};
    font-size: 14px;
    font-weight: 500;
`;

const ErrorMessage = styled.div`
    margin-top: 8px;
    color: ${COLORS['red-60']};
    font-size: 13px;
`;
